/**
 * An entity type that can be surfaced as a page-context Badge in the chat.
 * Only entities whose display value (reference / name / title) can be fetched
 * with an existing GraphQL query are listed — see `PageContextBadges.tsx`.
 */
export type BadgeEntityType =
  | 'project'
  | 'proposal'
  | 'estimate'
  | 'customer'
  | 'supplier'
  | 'pipeline';

type SegmentBadgeConfig = {
  type: BadgeEntityType;
  /** Human label shown on the badge, e.g. "Proposal". */
  label: string;
  /**
   * Label overrides keyed by the segment that follows the entity id, so a
   * sub-page can refine the label. e.g. `/pipelines/:id/config` reads
   * "Pipeline config".
   */
  subPageLabels?: Record<string, string>;
  /**
   * When true, the entity's data is scoped to its enclosing Project, so the
   * badge carries the nearest ancestor project id as `parentProjectId`.
   */
  scopedToProject?: boolean;
};

/**
 * Maps a collection path segment to the Badge it surfaces; the segment following
 * it is that entity's id, so `/projects/abc/proposals/xyz` yields a Project badge
 * (`abc`) and a Proposal badge (`xyz`).
 *
 * Only add a segment here once its entity has a resolver in
 * `PageContextBadges.tsx` that fetches a display value.
 */
export const URL_SEGMENT_BADGE_MAP: Record<string, SegmentBadgeConfig> = {
  projects: { type: 'project', label: 'Project' },
  // "deals" is the legacy path for a Project.
  deals: { type: 'project', label: 'Project' },
  proposals: { type: 'proposal', label: 'Proposal' },
  estimates: { type: 'estimate', label: 'Estimate', scopedToProject: true },
  customers: { type: 'customer', label: 'Customer' },
  suppliers: { type: 'supplier', label: 'Supplier' },
  pipelines: {
    type: 'pipeline',
    label: 'Pipeline',
    subPageLabels: { config: 'Manage pipeline' },
  },
};

/**
 * Segments that can sit in the id slot after a collection segment but are
 * route keywords, not entity ids (e.g. `/proposals/new`). These never produce
 * a Badge.
 */
const RESERVED_ID_SEGMENTS = new Set([
  'new',
  'archived',
  'quick',
  'edit',
  'config',
  'preview',
  'commissioning',
  'groups',
]);

/** A resolved page-context Badge to render above the chat input. */
export type PageContextBadge = {
  type: BadgeEntityType;
  /** Human label shown on the badge, refined by the sub-page where relevant. */
  label: string;
  /** The entity id lifted from the URL. */
  id: string;
  /** The nearest ancestor project id, for project-scoped entities. */
  parentProjectId?: string;
};

/**
 * Walks segment pairs left-to-right, so nested URLs yield badges in path order.
 * Reserved keyword ids and repeated entities are skipped.
 */
export function parsePageContextBadges(pathname: string): PageContextBadge[] {
  const segments = pathname.split('/').filter(Boolean);
  const badges: PageContextBadge[] = [];
  const seen = new Set<string>();
  let projectId: string | undefined;

  for (let i = 0; i < segments.length - 1; i++) {
    const config = URL_SEGMENT_BADGE_MAP[segments[i]];
    if (!config) continue;

    const id = segments[i + 1];
    if (RESERVED_ID_SEGMENTS.has(id)) continue;

    if (config.type === 'project') projectId = id;

    const key = `${config.type}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const subSegment = segments[i + 2];
    const label =
      (subSegment && config.subPageLabels?.[subSegment]) ?? config.label;

    badges.push({
      type: config.type,
      label,
      id,
      ...(config.scopedToProject ? { parentProjectId: projectId } : {}),
    });
  }

  return badges;
}
